import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../../../contexts/TenantContext';

const ThrowballLogin = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { tenant, siteSettings } = useTenant();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = `${tenant?.name || 'TFI'} | Admin Login`;

    // Check if already logged in
    const token = localStorage.getItem('tenant_admin_token');
    if (token) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [tenant, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const response = await fetch('/api/client-admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Original-Host': window.location.hostname
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        // Store token and user data
        localStorage.setItem('tenant_admin_token', data.access_token);
        localStorage.setItem('tenant_admin_user', JSON.stringify(data.user));

        // Security: a reactivated user must reset their password before access.
        if (data.password_reset_required) {
          localStorage.setItem('force_password_reset', '1');
        } else {
          localStorage.removeItem('force_password_reset');
        }

        // Redirect based on user type
        if (data.user.user_type === 'admin' || data.user.role_slug === 'admin') {
          // Tenant Admin - redirect to admin dashboard
          window.location.href = '/admin/dashboard';
        } else {
          // Regular User - redirect to user dashboard
          window.location.href = '/user/dashboard';
        }
      } else {
        if (data.errors) {
          setErrors(data.errors);
        } else {
          setErrors({
            general: data.message || 'Login failed. Please check your credentials.'
          });
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({
        general: 'Network error. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tb-login-page">
      <div className="tb-login-container">
        {/* Left Side - Branding */}
        <div className="tb-login-branding">
          <div className="tb-branding-content">
            <img
              src={siteSettings?.logo || '/theme/assets/images/logo.png'}
              alt={tenant?.name || 'TFI'}
              className="tb-login-logo"
            />
            <h1>{tenant?.name || 'Throwball Federation of India'}</h1>
            <p>Admin Portal</p>
            <div className="tb-branding-features">
              <div className="tb-feature">
                <i className="fas fa-users"></i>
                <span>Manage Players</span>
              </div>
              <div className="tb-feature">
                <i className="fas fa-user-tie"></i>
                <span>Manage Officials</span>
              </div>
              <div className="tb-feature">
                <i className="fas fa-trophy"></i>
                <span>Tournaments</span>
              </div>
              <div className="tb-feature">
                <i className="fas fa-certificate"></i>
                <span>Certificates</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="tb-login-form-wrapper">
          <div className="tb-login-form-container">
            <div className="tb-login-header">
              <h2>Welcome Back</h2>
              <p>Sign in to access your admin dashboard</p>
            </div>

            {errors.general && (
              <div className="tb-error-alert">
                <i className="fas fa-exclamation-circle"></i>
                <span>{errors.general}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="tb-login-form">
              <div className="tb-form-group">
                <label htmlFor="username">
                  <i className="fas fa-user"></i>
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Enter your username"
                  className={errors.username ? 'error' : ''}
                  required
                />
                {errors.username && (
                  <span className="tb-field-error">{errors.username[0]}</span>
                )}
              </div>

              <div className="tb-form-group">
                <label htmlFor="password">
                  <i className="fas fa-lock"></i>
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className={errors.password ? 'error' : ''}
                  required
                />
                {errors.password && (
                  <span className="tb-field-error">{errors.password[0]}</span>
                )}
              </div>

              <div className="tb-form-options">
                <label className="tb-checkbox">
                  <input type="checkbox" />
                  <span>Remember me</span>
                </label>
                <a href="/forgot-password" className="tb-forgot-link">
                  Forgot Password?
                </a>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="tb-login-btn"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Signing in...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt"></i>
                    Sign In
                  </>
                )}
              </button>
            </form>

            <div className="tb-login-footer">
              <p>Need help? <a href="/contact">Contact Support</a></p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .tb-login-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          position: relative;
        }

        .tb-login-page::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background:
            radial-gradient(circle at 20% 80%, rgba(224, 17, 25, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(224, 17, 25, 0.1) 0%, transparent 40%);
          pointer-events: none;
        }

        .tb-login-container {
          display: flex;
          max-width: 1000px;
          width: 100%;
          background: white;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 25px 60px -12px rgba(0, 0, 0, 0.5);
          position: relative;
          z-index: 1;
        }

        /* Left Side - Branding */
        .tb-login-branding {
          flex: 1;
          background: #c00f15;
          padding: 60px 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .tb-login-branding::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 50%);
          animation: pulse 4s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.3; }
        }

        .tb-branding-content {
          position: relative;
          z-index: 1;
          text-align: center;
        }

        .tb-login-logo {
          width: 120px;
          height: 120px;
          object-fit: contain;
          margin: 0 auto 24px auto;
          display: block;
          background: white;
          border-radius: 20px;
          padding: 10px;
        }

        .tb-branding-content h1 {
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 8px;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
          color: #ffffff !important;
        }

        .tb-branding-content > p {
          font-size: 16px;
          opacity: 0.9;
          margin-bottom: 40px;
          color: #ffffff !important;
        }

        .tb-branding-features {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .tb-feature {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: rgba(255,255,255,0.1);
          border-radius: 12px;
          font-size: 14px;
        }

        .tb-feature i {
          font-size: 18px;
        }

        /* Right Side - Form */
        .tb-login-form-wrapper {
          flex: 1;
          padding: 60px 50px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .tb-login-form-container {
          width: 100%;
          max-width: 400px;
        }

        .tb-login-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .tb-login-header h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 8px;
        }

        .tb-login-header p {
          color: #666;
          font-size: 15px;
        }

        .tb-error-alert {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 18px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 12px;
          color: #dc2626;
          margin-bottom: 24px;
          font-size: 14px;
        }

        .tb-error-alert i {
          font-size: 18px;
        }

        .tb-login-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .tb-form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .tb-form-group label {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .tb-form-group label i {
          color: #c00f15;
          font-size: 14px;
        }

        .tb-form-group input {
          padding: 14px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          font-size: 15px;
          transition: all 0.3s;
          outline: none;
        }

        .tb-form-group input:focus {
          border-color: #c00f15;
          box-shadow: 0 0 0 4px rgba(233, 30, 99, 0.1);
        }

        .tb-form-group input.error {
          border-color: #dc2626;
        }

        .tb-field-error {
          font-size: 13px;
          color: #dc2626;
        }

        .tb-form-options {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .tb-checkbox {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 14px;
          color: #4b5563;
        }

        .tb-checkbox input {
          width: 16px;
          height: 16px;
          accent-color: #c00f15;
        }

        .tb-forgot-link {
          font-size: 14px;
          color: #c00f15;
          text-decoration: none;
          font-weight: 500;
        }

        .tb-forgot-link:hover {
          text-decoration: underline;
        }

        .tb-login-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 16px;
          background: linear-gradient(135deg, #c00f15 0%, #c00f15 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          margin-top: 8px;
        }

        .tb-login-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(233, 30, 99, 0.4);
        }

        .tb-login-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .tb-login-footer {
          text-align: center;
          margin-top: 32px;
          padding-top: 24px;
          border-top: 1px solid #e5e7eb;
        }

        .tb-login-footer p {
          color: #6b7280;
          font-size: 14px;
        }

        .tb-login-footer a {
          color: #c00f15;
          text-decoration: none;
          font-weight: 500;
        }

        .tb-login-footer a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .tb-login-container {
            flex-direction: column;
          }

          .tb-login-branding {
            padding: 40px 30px;
          }

          .tb-branding-content h1 {
            font-size: 22px;
          }

          .tb-branding-features {
            display: none;
          }

          .tb-login-form-wrapper {
            padding: 40px 30px;
          }
        }
      `}</style>
    </div>
  );
};

export default ThrowballLogin;
